import {
  getConfigurationForSilo,
  getFullSiloConfiguration,
} from "./static/silo-config";
import { useCallback } from "react";
import * as R from "fp-ts/Record";
import * as O from "fp-ts/Option";
import { pipe } from "fp-ts/lib/function";
import { SiloConfig, siloConfig } from "./utilities/silo-config-option";
import {
  GetConfigurationForAsset,
  getStaticConfigForAssetForProtocolKey,
  useStaticAssetConfiguration,
} from "./useAssetConfiguration";
import {
  ChecksumAddress,
  makeAddressSync,
  ProtocolInstance,
  ProtocolKey,
} from "@silo/types";
import { isUndefined } from "lodash";
import { option } from "fp-ts";
import { assetConfig } from "./utilities/asset-config-option";
import merge from "lodash/merge";
import { getConfigDepositMode } from "@silo/config";

export const getStaticConfigForSiloForProtocolKey =
  (getConfigurationForAsset: GetConfigurationForAsset) =>
  (protocolKey: ProtocolKey): GetConfigurationForSilo =>
  (siloAddress) => {
    const config = getConfigurationForSilo(protocolKey, siloAddress);

    return {
      ...config,
      getConfigurationForAsset: (assetAddress) => {
        const _assetAddress = assetAddress;

        const siloAssetConfig = config.assets[_assetAddress];
        const protocolAssetConfig = getConfigurationForAsset(assetAddress);

        /**
         * siloAssetConfig overrides values from protocolAssetConfig
         */
        return merge({}, protocolAssetConfig, siloAssetConfig);
      },
    };
  };

/**
 * Returns a function to get silo configuration for a given silo address
 */
export const useSiloConfiguration = (
  protocolInstance: ProtocolInstance | undefined
) => {
  const staticConfiguration = useStaticSiloConfiguration(protocolInstance);

  return staticConfiguration;
};

export type GetConfigurationForSilo = (siloAddress: ChecksumAddress) => Omit<
  SiloConfig,
  "assets"
> & {
  whitelisted: O.Option<ProtocolKey>;
  getConfigurationForAsset: GetConfigurationForAsset;
};
export type GetFullSiloConfiguration = () => Record<
  ChecksumAddress,
  ReturnType<GetConfigurationForSilo>
>;
export type SiloConfiguration = ReturnType<GetConfigurationForSilo>;

export const useStaticSiloConfiguration = (
  protocolInstance: ProtocolInstance | undefined
): {
  getConfigurationForSilo: GetConfigurationForSilo;
  getFullSiloConfiguration: GetFullSiloConfiguration;
} => {
  const { getConfigurationForAsset } =
    useStaticAssetConfiguration(protocolInstance);

  const _getConfigurationForSilo: GetConfigurationForSilo = useCallback(
    (siloAddress) => {
      if (isUndefined(protocolInstance)) {
        // TODO: come up with a better way to get "default" silo config value
        return {
          ...siloConfig.create({}),
          whitelisted: option.none,
          getConfigurationForAsset: () => assetConfig.create({}),
        };
      }

      return getStaticConfigForSiloForProtocolKey(getConfigurationForAsset)(
        protocolInstance
      )(siloAddress);
    },
    [protocolInstance, getConfigurationForAsset]
  );

  const _getFullSiloConfiguration: GetFullSiloConfiguration =
    useCallback(() => {
      if (isUndefined(protocolInstance)) {
        return {};
      }

      return pipe(
        getFullSiloConfiguration(protocolInstance),
        R.mapWithIndex((key) => _getConfigurationForSilo(key))
      );
    }, [protocolInstance, _getConfigurationForSilo]);

  return {
    getConfigurationForSilo: _getConfigurationForSilo,
    getFullSiloConfiguration: _getFullSiloConfiguration,
  };
};

export const getSiloConfigForProtocolKey = (
  protocolKey: ProtocolKey
): GetConfigurationForSilo =>
  getStaticConfigForSiloForProtocolKey(
    getStaticConfigForAssetForProtocolKey(protocolKey)
  )(protocolKey);

export const getIsProtectedOnly =
  (protocolKey: ProtocolKey, siloAddress: ChecksumAddress) => (i: number) => {
    const marketAddress = makeAddressSync(siloAddress);

    if (i === 0) {
      const depositMode = getConfigDepositMode({
        protocolKey,
        marketAddress,
        assetKey: "base",
      });

      return depositMode === "protectedOnly" || depositMode === "defaultFalse";
    }

    if (i === 1) {
      const depositMode = getConfigDepositMode({
        protocolKey,
        marketAddress,
        assetKey: "firstBridge",
      });

      return depositMode === "protectedOnly" || depositMode === "defaultFalse";
    }

    if (i === 2) {
      const depositMode = getConfigDepositMode({
        protocolKey,
        marketAddress,
        assetKey: "secondBridge",
      });

      return depositMode === "protectedOnly" || depositMode === "defaultFalse";
    }

    return false;
  };
